<script>
import CodeEditor from '@/components/form-controls/CodeEditor';
import Panel from '@/components/ui/Panel';

export default {
  name: 'CodePanel',
  components: {
    CodeEditor,
    Panel
  },
  data() {
    return {
      editor: {},
      bookmark: null,
      codeBlock: null,
      language: '',
      source: ''
    };
  },
  computed: {
    currentLanguage() {
      const language = this.language.toLowerCase();

      if (['css'].includes(language)) return 'css';
      if (['javascript', 'js'].includes(language)) return 'javascript';
      if (['html', 'xml'].includes(language)) return 'html';
      if (['php'].includes(language)) return 'php';

      return 'none'; // unsupported languages
    }
  },
  methods: {
    show(options) {
      const editor = options.editor;
      const el = editor.getSelectedElement();
      this.bookmark = editor.getBookmark();
      this.editor = editor;
      this.codeBlock = el.closest('.cms-code');
      this.language = this.codeBlock ? this.codeBlock.getAttribute('data-language') : '';
      this.source = this.codeBlock ? this.codeBlock.getAttribute('data-source') : '';
      this.$refs.panel.show();
    },

    hide() {
      this.$refs.panel.hide();
      this.source = '';
    },

    removeCodeBlock() {
      this.$refs.panel.hide();
      this.editor.restoreBookmark(this.bookmark);
      this.editor.removeElement(this.codeBlock);
    },

    handleLanguageChange() {
      this.language = this.language
        .trim()
        .toLowerCase()
        .replace(/[^a-z-0-9-]+/g, '-');
    },

    handleHide() {
      this.editor.focus();
    },

    handleShow() {
      this.$refs.language.focus();
    },

    handleSubmit() {
      const source = this.source;
      const language = this.language;
      this.$refs.panel.hide();
      this.editor.restoreBookmark(this.bookmark);
      this.editor.insertCodeBlock(source, { language });
    }
  }
};
</script>

<template>
  <panel
    ref="panel"
    :title="$t('Code Block')"
    size="lg"
    @show="handleShow"
    @hide="handleHide"
  >
    <el-form
      label-position="top"
      novalidate
      @submit.native.prevent="handleSubmit"
    >
      <fieldset>
        <!-- Language -->
        <el-form-item :label="$t('Language[computer]')">
          <el-input
            ref="language"
            v-model="language"
            type="text"
            auto-complete="off"
            spellcheck="false"
            placeholder="html, css, javascript..."
            @change="handleLanguageChange"
          />
          <div class="text-form-description">
            {{ $t('Your syntax highlighter will use this to identify the programming language.') }}
          </div>
        </el-form-item>

        <!-- Source -->
        <el-form-item :label="$t('Source')">
          <code-editor
            ref="codeEditor"
            v-model="source"
            :allow-full-screen="false"
            :line-numbers="true"
            :mode="currentLanguage"
            height="auto"
          />
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-button type="primary" native-type="submit">
              <span v-if="codeBlock">{{ $t('Update Code Block') }}</span>
              <span v-else>{{ $t('Insert Code Block') }}</span>
            </el-button>
          </el-col>
          <el-col :span="12" class="text-right">
            <el-button v-if="codeBlock" :title="$t('Delete Code Block')" @click="removeCodeBlock">
              <i class="el-icon-fa-trash" />
            </el-button>
          </el-col>
        </el-row>
      </fieldset>
    </el-form>
  </panel>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

/deep/ .code-editor {
  .CodeMirror {
    min-height: 200px;
  }

  .CodeMirror-scroll {
    max-height: calc(100vh - 23rem);

    @include breakpoint('sm-and-down') {
      max-height: calc(100vh - 22rem);
    }
  }
}
</style>
